import React, { useEffect, useState, useMemo } from 'react';
import { Col, Row, getItemWithExpiration } from '../lib/utils';
import { colors } from '../lib/colors';
import Grid, { GridColProps } from '../components/Grid/Grid';
import ModalAlert from '../components/modal/ModalAlert';
import SvgIcon from '../components/SvgIcon';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import BusinessInfoFooter from '../components/BusinessInfoFooter';
import { usePublicMemberList, useDeletePublicMember, useMemberArchiveList } from '@/features/publicMember/hooks';
import { publicMemberKeys } from '@/features/publicMember/keys';
import {
    APP_ACCOUNT_STATUS_LABEL,
    AppAccountStatus,
    FacilityStatus,
    ICenterMember,
    MemberType,
    getMemberAppAccountStatus,
    isMemberAppLinked,
} from '@/features/publicMember/types';
import { getCenterMembers } from '@/features/publicMember/api';
import PublicMemberBulkUploadModal from '../components/PublicMember/PublicMemberBulkUploadModal';
import MemberTypeConvertModal from '../components/PublicMember/MemberTypeConvertModal';
import ActivePassSummary, { ActivePassSummaryItem } from '../components/MemberList/ActivePassSummary';
import AppAccountStatusBadge from '../components/MemberList/AppAccountStatusBadge';
import useHoverCard from '../hook/useHoverCard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCoachNameListAPI } from '../api/center';
import { formatKoreanPhoneNumber } from '../utils/phoneNumber';
import { memberDetailPath, memberDetailState } from '../lib/memberRoutes';

/* ═══════ 상수 ═══════ */

const MEMBER_TYPE_OPTIONS: {
    code: MemberType | 'all' | 'archived' | 'pt' | 'group';
    value: string;
    icon: string;
}[] = [
    { code: 'all', value: '전체', icon: '📂' },
    { code: 'original', value: '수강 회원', icon: '🏋️' },
    { code: 'pt', value: '활성 PT 회원', icon: '👤' },
    { code: 'group', value: '활성 그룹 회원', icon: '👥' },
    { code: 'public', value: '퍼블릭 회원', icon: '🌐' },
    { code: 'archived', value: '과거 회원', icon: '📋' },
];

const MEMBER_TYPE_LABEL: Record<string, string> = {
    public: '퍼블릭 회원',
    temp: '수강 회원',
    original: '수강 회원',
};

const DEFAULT_MEMBER_FETCH_LIMIT = 200;
const DEFAULT_MEMBER_LIST_PAGE_LIMIT = 10;
const MEMBER_LIST_PAGE_SIZE_OPTIONS = [10, 30, 50, 100] as const;
const EXCEL_EMPTY_VALUE = '-';
const EXCEL_LINE_BREAK = '\n';
const APP_ACCOUNT_FILTER_LABEL: Record<AppAccountStatus | 'all', string> = {
    all: '전체',
    ...APP_ACCOUNT_STATUS_LABEL,
};
const FACILITY_STATUS_FILTER_LABEL: Record<FacilityStatus | 'all', string> = {
    all: '전체',
    active: '이용 중',
    expiring: '7일 안에 만료',
    expired: '만료',
};

type PTMembershipForExcel = NonNullable<ICenterMember['memberships']>[number];
type GroupTicketForExcel = NonNullable<ICenterMember['groupTickets']>[number];
type SubscriptionForExcel = NonNullable<ICenterMember['subscriptions']>[number];
type TicketWithStatus = { status?: string };

const getTicketStatusLabel = (status?: string) => {
    switch (status) {
        case 'active':
            return '활성';
        case 'expired':
            return '만료';
        case 'paused':
            return '정지';
        case 'canceled':
        case 'cancelled':
            return '취소';
        case 'pending':
            return '대기';
        default:
            return status || '';
    }
};

const formatExcelDate = (date?: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '');

const formatExcelPeriod = (startDate?: string, endDate?: string) => {
    const start = formatExcelDate(startDate);
    const end = formatExcelDate(endDate);
    if (start && end) return `${start} ~ ${end}`;
    if (start) return `${start} ~`;
    if (end) return `~ ${end}`;
    return '';
};

const joinExcelLines = (values: Array<string | number | null | undefined>, fallback = EXCEL_EMPTY_VALUE) => {
    const lines = values.map((value) => (value === null || value === undefined ? '' : String(value).trim())).filter(Boolean);
    return lines.length ? lines.join(EXCEL_LINE_BREAK) : fallback;
};

const formatTicketNameForExcel = (name?: string, status?: string) => {
    const label = getTicketStatusLabel(status);
    const baseName = name || '이름 없음';
    return label && label !== '활성' ? `${baseName} (${label})` : baseName;
};

const formatSessionValue = (value?: number) => (typeof value === 'number' ? `${value}회` : '');

const formatCurrentTotalSession = (current?: number, total?: number) => {
    const currentText = typeof current === 'number' ? current : EXCEL_EMPTY_VALUE;
    const totalText = typeof total === 'number' ? total : EXCEL_EMPTY_VALUE;
    return `${currentText} / ${totalText}`;
};

const isActiveTicket = <T extends TicketWithStatus>(ticket: T) => ticket.status === 'active';

const formatSummaryPeriod = (startDate?: string, endDate?: string) => {
    const start = startDate ? dayjs(startDate).format('YY.MM.DD') : '';
    const end = endDate ? dayjs(endDate).format('YY.MM.DD') : '';
    if (start && end) return `${start} ~ ${end}`;
    if (start) return `${start} ~`;
    if (end) return `~ ${end}`;
    return undefined;
};

const toPtPassSummaryItems = (memberships: PTMembershipForExcel[] = []): ActivePassSummaryItem[] =>
    memberships.filter(isActiveTicket).map((membership) => ({
        id: membership.membershipId,
        name: membership.name,
        current: membership.currentSession,
        total: membership.totalSession,
        remaining: membership.remainSession,
        coachName: membership.coachName,
        periodText: formatSummaryPeriod(membership.activationDate, membership.expirationDate),
        expiresAt: membership.expirationDate,
    }));

const getGroupTicketTotalCount = (ticket: GroupTicketForExcel) => {
    const total = Number(ticket.totalCount ?? 0);
    const used = Number(ticket.usedCount ?? 0);
    const remaining = Number(ticket.remainingCount ?? 0);
    return Math.max(total, used + remaining, 0);
};

const toGroupPassSummaryItems = (tickets: GroupTicketForExcel[] = []): ActivePassSummaryItem[] =>
    tickets.filter(isActiveTicket).map((ticket) => {
        const total = getGroupTicketTotalCount(ticket);
        const remaining = Math.max(Number(ticket.remainingCount ?? 0), 0);
        return {
            id: ticket.memberTicketId,
            name: ticket.name,
            current: total > 0 ? Math.min(Math.max(total - remaining, 0), total) : undefined,
            total,
            remaining,
            periodText: formatSummaryPeriod(ticket.startDate, ticket.expiryDate),
            expiresAt: ticket.expiryDate,
        };
    });

const formatDomesticPhoneNumber = (phone?: string) => {
    return formatKoreanPhoneNumber(phone, EXCEL_EMPTY_VALUE);
};

/* ═══════ 호버 팝오버 컴포넌트 ═══════ */

const HoverPopover = ({ trigger, children }: { trigger: React.ReactNode; children: React.ReactNode }) => {
    const { anchorRef, rect, anchorProps } = useHoverCard<HTMLDivElement>();

    return (
        <div ref={anchorRef} className="inline-block" {...anchorProps}>
            {trigger}
            {rect && (
                <div
                    style={{
                        position: 'fixed',
                        top: rect.top - 8,
                        left: rect.left + rect.width / 2,
                        transform: 'translate(-50%, -100%)',
                        padding: '16px 18px',
                        background: 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 100%)',
                        border: '1px solid rgba(148,163,184,0.18)',
                        color: 'white',
                        borderRadius: 16,
                        fontSize: 13,
                        lineHeight: 1.65,
                        whiteSpace: 'nowrap',
                        zIndex: 9999,
                        boxShadow: '0 18px 42px rgba(15,23,42,0.35)',
                        minWidth: 240,
                        maxWidth: 320,
                        backdropFilter: 'blur(8px)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {children}
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid rgba(30,41,59,0.98)',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

/* ═══════ 배지 컴포넌트 ═══════ */
const Badge = ({ label, bg, color, border, size = 'md' }: { label: string; bg: string; color: string; border?: string; size?: 'sm' | 'md' }) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: size === 'sm' ? '4px 10px' : '6px 12px',
            borderRadius: 999,
            fontSize: size === 'sm' ? 12 : 13,
            fontWeight: 700,
            backgroundColor: bg,
            color: color,
            border: border ? `1px solid ${border}` : 'none',
            letterSpacing: -0.2,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
        }}
    >
        {label}
    </span>
);

const EmptyCell = () => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 34,
                height: 24,
                padding: '0 10px',
                borderRadius: 999,
                backgroundColor: '#F8FAFC',
                color: '#CBD5E1',
                fontSize: 13,
                fontWeight: 700,
            }}
        >
            —
        </span>
    </div>
);

/* ═══════ 메인 컴포넌트 ═══════ */

const MemberlistPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const centerId = authObject?.centerId;
    const navigate = useNavigate();

    // ── 필터 상태 ──
    const [memberTypeFilter, setMemberTypeFilter] = useState<MemberType | 'all' | 'archived' | 'pt' | 'group'>('all');
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_memberList') !== 'hidden');
    const [coachFilter, setCoachFilter] = useState<string>('all');
    const [periodFilter, setPeriodFilter] = useState<string>('all');
    const [sessionsFilter, setSessionsFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [facilityFilter, setFacilityFilter] = useState<FacilityStatus | 'all'>('all');
    const [appAccountStatusFilter, setAppAccountStatusFilter] = useState<AppAccountStatus | 'all'>('all');
    const [showFilters, setShowFilters] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(DEFAULT_MEMBER_LIST_PAGE_LIMIT);
    const [archiveFetchLimit, setArchiveFetchLimit] = useState(DEFAULT_MEMBER_FETCH_LIMIT);

    const hasAdvancedFilter =
        coachFilter !== 'all' ||
        periodFilter !== 'all' ||
        sessionsFilter !== 'all' ||
        statusFilter !== 'all' ||
        facilityFilter !== 'all' ||
        appAccountStatusFilter !== 'all';

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 350);
        return () => window.clearTimeout(timer);
    }, [searchValue]);

    // 전화번호로 추정되는 검색어(+/숫자 위주)면 공백/하이픈 제거해서 서버 저장 포맷과 맞춤
    const normalizedSearch = (() => {
        const v = debouncedSearchValue.trim();
        if (!v) return undefined;
        // 숫자/+가 절반 이상이면 전화번호로 간주
        const digitOrPlus = (v.match(/[0-9+]/g) || []).length;
        if (digitOrPlus >= Math.ceil(v.length / 2)) {
            return v.replace(/[\s-]/g, '');
        }
        return v;
    })();

    // ── V1 API ──
    const isArchiveTab = memberTypeFilter === 'archived';
    const queryClient = useQueryClient();
    const serverMemberScope =
        memberTypeFilter === 'original'
            ? 'lesson'
            : memberTypeFilter === 'pt' || memberTypeFilter === 'group'
              ? memberTypeFilter
              : memberTypeFilter === 'public'
                ? 'public'
                : 'all';
    const {
        data: memberData,
        isLoading: isMemberLoading,
        isFetching: isMemberFetching,
    } = usePublicMemberList({
        centerId,
        memberScope: serverMemberScope,
        search: normalizedSearch,
        coach: coachFilter,
        remainingPeriod: periodFilter,
        remainingSessions: sessionsFilter,
        displayStatus: statusFilter as 'active' | 'pending' | 'expired' | 'all',
        facilityStatus: facilityFilter,
        appAccountStatus: appAccountStatusFilter,
        page,
        limit: pageLimit,
        enabled: !isArchiveTab,
    });
    const { data: archiveData, isLoading: isArchiveLoading } = useMemberArchiveList({
        centerId,
        search: normalizedSearch,
        page,
        limit: archiveFetchLimit,
        enabled: isArchiveTab,
    });
    // 과거 회원 탭 노출 여부 판단용 (limit 1로 가볍게 존재만 확인)
    const { data: archiveExistCheck } = useMemberArchiveList({
        centerId,
        page: 1,
        limit: 1,
        enabled: !isArchiveTab && !!centerId,
    });
    const hasArchives = (archiveExistCheck?.totalResults ?? 0) > 0;

    useEffect(() => {
        if (isArchiveTab) {
            setArchiveFetchLimit(DEFAULT_MEMBER_FETCH_LIMIT);
        }
    }, [centerId, isArchiveTab, normalizedSearch]);

    useEffect(() => {
        if (!isArchiveTab) return;
        const nextLimit = archiveData?.totalResults ?? 0;
        if (nextLimit > archiveFetchLimit) {
            setArchiveFetchLimit(nextLimit);
        }
    }, [archiveData?.totalResults, archiveFetchLimit, isArchiveTab]);

    const handleRefresh = () => {
        queryClient.invalidateQueries({
            queryKey: publicMemberKeys.list(centerId),
        });
        queryClient.invalidateQueries({
            queryKey: ['coachNameListForFilter', centerId],
        });
    };

    // ── 강사 목록 ──
    const { data: coachListData } = useQuery(['coachNameListForFilter', centerId], () => getCoachNameListAPI(centerId, authorization), {
        enabled: !!centerId && !!authorization,
    });
    // 백엔드 응답 shape 방어: response.data.coachNameList / response.coachNameList / response.data (배열) 순서로 fallback
    const coachList: any[] =
        (coachListData as any)?.data?.coachNameList ??
        (coachListData as any)?.coachNameList ??
        (Array.isArray((coachListData as any)?.data) ? (coachListData as any).data : []) ??
        [];

    const allMembers = useMemo(() => memberData?.results ?? [], [memberData]);

    const members = allMembers;

    const totalResults = memberData?.totalResults ?? 0;
    const memberCategoryCounts = memberData?.memberCategoryCounts;
    const publicMemberCount = memberCategoryCounts?.public ?? allMembers.filter((member) => member.memberType === 'public').length;
    const lessonMemberCount =
        memberCategoryCounts?.lesson ?? allMembers.filter((member) => member.memberType === 'original' || member.memberType === 'temp').length;
    const displayedTotalResults = isArchiveTab ? (archiveData?.totalResults ?? 0) : totalResults;
    const visibleMemberCount = isArchiveTab ? (archiveData?.results?.length ?? 0) : members.length;
    const isArchiveListIncomplete = isArchiveTab && (archiveData?.totalResults ?? 0) > (archiveData?.results?.length ?? 0);
    const totalMemberPages = Math.max(1, memberData?.totalPages ?? 1);
    const isMemberRefetching = !isArchiveTab && isMemberFetching && !isMemberLoading;

    const deleteMutation = useDeletePublicMember();

    // ── UI 상태 ──
    // memberId Set + snapshot Map 으로 선택을 관리해서 페이지 이동·검색·필터에도 선택이 유지되도록 함.
    // 참조 비교가 아닌 ID 비교라서 데이터 refetch 로 객체 참조가 바뀌어도 안전.
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectedMemberMap, setSelectedMemberMap] = useState<Map<string, ICenterMember>>(new Map());
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isBulkUploadVisible, setIsBulkUploadVisible] = useState(false);
    const [convertTarget, setConvertTarget] = useState<ICenterMember | null>(null);
    const [isExportingAll, setIsExportingAll] = useState(false);

    const selectedMembers = useMemo(
        () => Array.from(selectedMemberMap.values()),
        [selectedMemberMap]
    );

    const toggleSelect = (memberId: string, member?: ICenterMember) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(memberId)) next.delete(memberId);
            else next.add(memberId);
            return next;
        });
        setSelectedMemberMap((prev) => {
            const next = new Map(prev);
            if (next.has(memberId)) next.delete(memberId);
            else if (member) next.set(memberId, member);
            return next;
        });
    };

    const toggleAllVisible = (visibleIds: string[], visibleRows: ICenterMember[] = []) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const allSelected = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
            if (allSelected) visibleIds.forEach((id) => next.delete(id));
            else visibleIds.forEach((id) => next.add(id));
            return next;
        });
        setSelectedMemberMap((prev) => {
            const next = new Map(prev);
            const allSelected = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
            if (allSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleRows.forEach((row) => next.set(row.memberId, row));
            }
            return next;
        });
    };

    const removeFromSelection = (memberId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(memberId);
            return next;
        });
        setSelectedMemberMap((prev) => {
            const next = new Map(prev);
            next.delete(memberId);
            return next;
        });
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        setSelectedMemberMap(new Map());
    };

    const handleDeleteSelected = async () => {
        const targetIds = Array.from(selectedIds);
        const results = await Promise.all(
            targetIds.map(async (memberId) => {
                try {
                    await deleteMutation.mutateAsync({ centerId, memberId });
                    return { memberId, succeeded: true };
                } catch {
                    return { memberId, succeeded: false };
                }
            })
        );
        const failedIds = new Set(
            results.filter((result) => !result.succeeded).map((result) => result.memberId)
        );

        if (failedIds.size === 0) {
            setIsDeleteModalVisible(false);
            clearSelection();
            createAlert('삭제 완료', '선택한 회원이 삭제되었습니다.', false);
            return;
        }

        setSelectedIds(failedIds);
        setSelectedMemberMap((previous) =>
            new Map(
                Array.from(previous.entries()).filter(([memberId]) => failedIds.has(memberId))
            )
        );
        createAlert(
            '일부 회원 삭제 실패',
            `${failedIds.size}명의 삭제에 실패했습니다. 실패한 회원만 다시 선택되어 있습니다.`,
            true
        );
    };

    const getMemberDisplayStatus = (row: ICenterMember): { label: string; bg: string; color: string; border: string } => {
        switch (row.ptStatus) {
            case 'active':
                return {
                    label: '활성',
                    bg: '#ECFDF5',
                    color: '#059669',
                    border: '#A7F3D0',
                };
            case 'expired':
                return {
                    label: '만료',
                    bg: '#FEF2F2',
                    color: '#DC2626',
                    border: '#FECACA',
                };
            case 'empty':
                return {
                    label: '대기',
                    bg: '#FFFBEB',
                    color: '#D97706',
                    border: '#FDE68A',
                };
            case 'none':
            default:
                if (row.hasSubscription)
                    return {
                        label: '활성',
                        bg: '#ECFDF5',
                        color: '#059669',
                        border: '#A7F3D0',
                    };
                if ((row.groupTicketCount ?? 0) > 0)
                    return {
                        label: '활성',
                        bg: '#ECFDF5',
                        color: '#059669',
                        border: '#A7F3D0',
                    };
                if (row.subscriptionCount > 0)
                    return {
                        label: '만료',
                        bg: '#FEF2F2',
                        color: '#DC2626',
                        border: '#FECACA',
                    };
                if (row.hasGroupTicket && (row.groupTicketCount ?? 0) === 0)
                    return {
                        label: '만료',
                        bg: '#FEF2F2',
                        color: '#DC2626',
                        border: '#FECACA',
                    };
                return {
                    label: '없음',
                    bg: '#F9FAFB',
                    color: '#9CA3AF',
                    border: '#E5E7EB',
                };
        }
    };

    const dashboardStats = useMemo(
        () => [
            {
                label: '조회 회원',
                value: displayedTotalResults.toLocaleString(),
                helper:
                    isArchiveListIncomplete
                        ? '전체 목록 동기화 중'
                        : hasAdvancedFilter
                          ? `필터 결과 중 현재 페이지 ${visibleMemberCount.toLocaleString()}명`
                          : visibleMemberCount !== displayedTotalResults
                            ? `현재 페이지 ${visibleMemberCount.toLocaleString()}명`
                            : '현재 조회 기준 전체 회원 수',
                accent: '#2563EB',
                bg: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                border: '#BFDBFE',
            },
            {
                label: '수강 회원',
                value: lessonMemberCount.toLocaleString(),
                helper: 'PT 또는 그룹수업 관리 대상',
                accent: '#059669',
                bg: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                border: '#A7F3D0',
            },
            {
                label: '퍼블릭 회원',
                value: publicMemberCount.toLocaleString(),
                helper: '퍼블릭 전용 프로필 관리 대상',
                accent: '#7C3AED',
                bg: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
                border: '#DDD6FE',
            },
            {
                label: '앱 연결',
                value: allMembers.filter((member) => isMemberAppLinked(member)).length.toLocaleString(),
                helper: '현재 페이지 기준 앱 연동 회원',
                accent: '#C2410C',
                bg: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
                border: '#FED7AA',
            },
        ],
        [allMembers, displayedTotalResults, hasAdvancedFilter, lessonMemberCount, publicMemberCount, visibleMemberCount, isArchiveListIncomplete]
    );

    const renderEmptyState = ({
        icon,
        title,
        description,
        actionLabel,
        actionOnClick,
    }: {
        icon: string;
        title: string;
        description: string;
        actionLabel?: string;
        actionOnClick?: () => void;
    }) => (
        <Col
            className="items-center justify-center"
            style={{
                padding: '72px 24px',
                gap: 12,
                minHeight: 360,
                borderRadius: 20,
                border: '1px dashed #CBD5E1',
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                textAlign: 'center',
            }}
        >
            <div style={{ fontSize: 44 }}>{icon}</div>
            <div
                style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#111827',
                    letterSpacing: -0.5,
                }}
            >
                {title}
            </div>
            <div
                style={{
                    maxWidth: 420,
                    fontSize: 15,
                    lineHeight: 1.65,
                    color: '#6B7280',
                }}
            >
                {description}
            </div>
            {actionLabel && actionOnClick && (
                <button
                    onClick={actionOnClick}
                    style={{
                        marginTop: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 44,
                        padding: '0 18px',
                        borderRadius: 12,
                        border: '1px solid #BFDBFE',
                        backgroundColor: '#EFF6FF',
                        color: '#1D4ED8',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}
                >
                    {actionLabel}
                </button>
            )}
        </Col>
    );

    const renderMemberServerPagination = () => {
        if (isArchiveTab) return <></>;

        const totalPages = totalMemberPages;
        const maxVisiblePages = 5;
        const halfVisible = Math.floor(maxVisiblePages / 2);
        let startPage = Math.max(page - halfVisible, 1);
        let endPage = Math.min(page + halfVisible, totalPages);

        if (page - halfVisible < 1) {
            endPage = Math.min(maxVisiblePages, totalPages);
        }
        if (page + halfVisible > totalPages) {
            startPage = Math.max(totalPages - maxVisiblePages + 1, 1);
        }

        const movePage = (nextPage: number) => {
            const boundedPage = Math.min(Math.max(nextPage, 1), totalPages);
            if (boundedPage !== page) setPage(boundedPage);
        };

        const controlButtonStyle = (disabled: boolean): React.CSSProperties => ({
            minWidth: 34,
            height: 34,
            padding: '0 8px',
            borderRadius: 8,
            border: '1px solid #E2E8F0',
            backgroundColor: disabled ? '#F8FAFC' : '#FFFFFF',
            color: disabled ? '#CBD5E1' : '#64748B',
            fontSize: 12,
            fontWeight: 800,
            cursor: disabled ? 'not-allowed' : 'pointer',
        });

        const pageButtons = [];
        for (let i = startPage; i <= endPage; i += 1) {
            const isCurrent = i === page;
            pageButtons.push(
                <button
                    key={i}
                    onClick={() => movePage(i)}
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        border: isCurrent ? '1px solid #BFDBFE' : '1px solid transparent',
                        backgroundColor: isCurrent ? '#EFF6FF' : 'transparent',
                        color: isCurrent ? '#2563EB' : '#475569',
                        fontSize: 13,
                        fontWeight: 800,
                        cursor: isCurrent ? 'default' : 'pointer',
                    }}
                >
                    {i}
                </button>
            );
        }

        return (
            <Row className="items-center justify-center" style={{ gap: 6, position: 'relative' }}>
                <button style={controlButtonStyle(page === 1)} disabled={page === 1} onClick={() => movePage(1)}>
                    {'<<'}
                </button>
                <button style={controlButtonStyle(page === 1)} disabled={page === 1} onClick={() => movePage(page - 1)}>
                    {'<'}
                </button>
                {startPage > 1 && <span style={{ color: '#94A3B8', padding: '0 4px' }}>...</span>}
                {pageButtons}
                {endPage < totalPages && <span style={{ color: '#94A3B8', padding: '0 4px' }}>...</span>}
                <button style={controlButtonStyle(page === totalPages)} disabled={page === totalPages} onClick={() => movePage(page + 1)}>
                    {'>'}
                </button>
                <button style={controlButtonStyle(page === totalPages)} disabled={page === totalPages} onClick={() => movePage(totalPages)}>
                    {'>>'}
                </button>
                {isMemberRefetching && (
                    <span
                        style={{
                            position: 'absolute',
                            top: 38,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: 11,
                            fontWeight: 800,
                            color: '#2563EB',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        목록 갱신 중
                    </span>
                )}
            </Row>
        );
    };

    const exportToExcel = (targetMembers: ICenterMember[] = members) => {
        if (!targetMembers.length) {
            createAlert('내보낼 데이터가 없습니다.', '회원 목록이 비어있습니다.', false);
            return;
        }
        const headers = [
            '이름',
            '회원타입',
            '상태',
            '앱 상태',
            '앱 탈퇴일',
            '휴대폰번호',
            '담당 강사',
            'PT 수강권',
            'PT 강사',
            'PT 회차(현재/총)',
            'PT 남은회차',
            'PT 기간',
            '그룹수강권',
            '그룹 회차(현재/총)',
            '그룹 남은회차',
            '그룹 기간',
            '이용권',
            '이용권 기간',
            '성별',
            '최근 수업일',
            '가입일',
        ];
        const excelData = targetMembers.map((m) => {
            const allPtList: PTMembershipForExcel[] = m.memberships || [];
            const allGroupList: GroupTicketForExcel[] = m.groupTickets || [];
            const ptList = allPtList.filter(isActiveTicket);
            const groupList = allGroupList.filter(isActiveTicket);
            const subscriptionList: SubscriptionForExcel[] = m.subscriptions || [];
            const appAccountStatus = getMemberAppAccountStatus(m);
            const ptFallback = allPtList.length === 0 && (m.hasPT || (m.ptCount ?? 0) > 0) ? `${m.ptCount || 1}개 보유` : EXCEL_EMPTY_VALUE;
            const groupFallback = allGroupList.length === 0 && (m.groupTicketCount ?? 0) > 0 ? `${m.groupTicketCount}개 보유` : EXCEL_EMPTY_VALUE;
            const subscriptionFallback = m.hasSubscription || (m.subscriptionCount ?? 0) > 0 ? `${m.subscriptionCount || 1}개 보유` : EXCEL_EMPTY_VALUE;

            return [
                m.name || '',
                MEMBER_TYPE_LABEL[m.memberType] || m.memberType,
                getMemberDisplayStatus(m).label,
                APP_ACCOUNT_STATUS_LABEL[appAccountStatus],
                appAccountStatus === 'withdrawn' && m.appWithdrawnAt
                    ? dayjs(m.appWithdrawnAt).format('YYYY-MM-DD')
                    : EXCEL_EMPTY_VALUE,
                formatDomesticPhoneNumber(m.phoneNumber),
                m.memberType === 'public' ? EXCEL_EMPTY_VALUE : m.coachName || EXCEL_EMPTY_VALUE,
                joinExcelLines(
                    ptList.map((ms) => formatTicketNameForExcel(ms.name, ms.status)),
                    ptFallback
                ),
                joinExcelLines(
                    ptList.map((ms) => ms.coachName),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    ptList.map((ms) => formatCurrentTotalSession(ms.currentSession, ms.totalSession)),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    ptList.map((ms) => formatSessionValue(ms.remainSession)),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    ptList.map((ms) => formatExcelPeriod(ms.activationDate, ms.expirationDate)),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    groupList.map((gt) => formatTicketNameForExcel(gt.name, gt.status)),
                    groupFallback
                ),
                joinExcelLines(
                    groupList.map((gt) => formatCurrentTotalSession(gt.usedCount, gt.totalCount)),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    groupList.map((gt) => formatSessionValue(gt.remainingCount)),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    groupList.map((gt) => formatExcelPeriod(gt.startDate, gt.expiryDate)),
                    EXCEL_EMPTY_VALUE
                ),
                joinExcelLines(
                    subscriptionList.map((sub) => sub.productName),
                    subscriptionFallback
                ),
                joinExcelLines(
                    subscriptionList.map((sub) => formatExcelPeriod(sub.startDate, sub.endDate)),
                    EXCEL_EMPTY_VALUE
                ),
                m.gender === 'male' ? '남성' : m.gender === 'female' ? '여성' : EXCEL_EMPTY_VALUE,
                m.lastLessonAt ? dayjs(m.lastLessonAt).format('YYYY-MM-DD') : EXCEL_EMPTY_VALUE,
                m.joinedAt ? dayjs(m.joinedAt).format('YYYY-MM-DD') : '',
            ];
        });

        const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
        ws['!cols'] = [
            { wch: 15 },
            { wch: 12 },
            { wch: 8 },
            { wch: 12 },
            { wch: 15 },
            { wch: 18 },
            { wch: 12 },
            { wch: 24 },
            { wch: 14 },
            { wch: 16 },
            { wch: 12 },
            { wch: 24 },
            { wch: 24 },
            { wch: 16 },
            { wch: 12 },
            { wch: 24 },
            { wch: 24 },
            { wch: 24 },
            { wch: 8 },
            { wch: 15 },
            { wch: 15 },
        ];
        const headerRange = XLSX.utils.decode_range(ws['!ref'] || 'A1');
        for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!ws[cellAddress]) continue;
            ws[cellAddress].s = {
                font: { bold: true, sz: 12 },
                fill: { fgColor: { rgb: 'E8F4FD' } },
                alignment: { horizontal: 'center', vertical: 'center' },
            };
        }
        ws['!rows'] = [
            { hpt: 24 },
            ...excelData.map((row) => {
                const maxLineCount = row.reduce((max, value) => {
                    const lineCount = String(value ?? '').split(EXCEL_LINE_BREAK).length;
                    return Math.max(max, lineCount);
                }, 1);
                return { hpt: Math.min(96, Math.max(24, maxLineCount * 18)) };
            }),
        ];
        for (let row = 1; row <= headerRange.e.r; row++) {
            for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (!ws[cellAddress]) continue;
                ws[cellAddress].s = {
                    ...(ws[cellAddress].s || {}),
                    alignment: {
                        vertical: 'top',
                        wrapText: true,
                    },
                };
            }
        }
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '센터회원목록');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `센터회원목록_${dayjs().format('YYYY-MM-DD')}.xlsx`);
        createAlert('엑셀 다운로드 완료', '회원 목록이 다운로드되었습니다.', false);
    };

    const exportAllMembersToExcel = async () => {
        if (isExportingAll) return;
        if (!displayedTotalResults) {
            createAlert('내보낼 데이터가 없습니다.', '회원 목록이 비어있습니다.', false);
            return;
        }

        setIsExportingAll(true);
        try {
            const exportLimit = Math.min(Math.max(displayedTotalResults, pageLimit), 5000);
            const { data } = await getCenterMembers({
                centerId,
                memberScope: serverMemberScope,
                search: normalizedSearch,
                coach: coachFilter,
                remainingPeriod: periodFilter,
                remainingSessions: sessionsFilter,
                displayStatus: statusFilter as 'active' | 'pending' | 'expired' | 'all',
                facilityStatus: facilityFilter,
                appAccountStatus: appAccountStatusFilter,
                page: 1,
                limit: exportLimit,
                timeout: 60_000,
            });
            const exportMembers = data?.results ?? [];
            exportToExcel(exportMembers);
            if (displayedTotalResults > exportLimit) {
                createAlert(
                    '일부만 내보냈습니다',
                    `현재 화면 조건의 회원이 ${displayedTotalResults.toLocaleString()}명이라 최대 ${exportLimit.toLocaleString()}명까지 내보냈습니다.`,
                    false
                );
            }
        } catch {
            createAlert('엑셀 다운로드 실패', '회원 목록을 다시 불러오지 못했습니다. 잠시 후 다시 시도해주세요.', true);
        } finally {
            setIsExportingAll(false);
        }
    };

    /* ═══════ 컬럼 정의 ═══════ */

    const colDefs: GridColProps[] = [
        {
            field: 'name',
            headerName: '이름',
            width: 150,
            align: 'center',
            isSort: true,
            fontType: 'Body2Bold',
        },
        {
            field: 'memberType',
            headerName: '회원타입',
            width: 120,
            align: 'center',
            customComponent: (value: any) => {
                const cfg: Record<string, { label: string; bg: string; color: string; border: string }> = {
                    public: {
                        label: '퍼블릭 회원',
                        bg: '#EFF6FF',
                        color: '#2563EB',
                        border: '#BFDBFE',
                    },
                    temp: {
                        label: '수강 회원',
                        bg: '#ECFDF5',
                        color: '#059669',
                        border: '#A7F3D0',
                    },
                    original: {
                        label: '수강 회원',
                        bg: '#ECFDF5',
                        color: '#059669',
                        border: '#A7F3D0',
                    },
                };
                const c = cfg[value] || {
                    label: value,
                    bg: '#F9FAFB',
                    color: '#6B7280',
                    border: '#E5E7EB',
                };
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge label={c.label} bg={c.bg} color={c.color} border={c.border} />
                    </div>
                );
            },
        },
        {
            field: 'phoneNumber',
            headerName: '휴대폰 번호',
            width: 170,
            align: 'center',
            nullValue: '-',
            fontType: 'Body2Regular',
            customFormat: (value: string) => formatDomesticPhoneNumber(value),
        },
        {
            field: 'appAccountStatus',
            headerName: '앱 상태',
            width: 112,
            align: 'center',
            customComponent: (_: any, row: ICenterMember) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <AppAccountStatusBadge member={row} showOnboardingStatus />
                </div>
            ),
        },
        {
            field: 'coachName',
            headerName: '담당 강사',
            width: 132,
            align: 'center',
            customComponent: (value: string, row: any) => {
                if ((row.ptStatus === 'none' && row.memberType === 'public') || !value) {
                    return <EmptyCell />;
                }
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge label={value} bg="#F0FDF4" color="#15803D" border="#BBF7D0" />
                    </div>
                );
            },
        },
        {
            field: 'memberships',
            headerName: 'PT 수강권',
            width: 360,
            align: 'center',
            customComponent: (memberships: any, row: ICenterMember) => {
                const allMs = memberships || [];
                const summaryItems = toPtPassSummaryItems(allMs);
                const count = allMs.length > 0 ? summaryItems.length : (row.ptCount ?? 0);

                if (count === 0) return <EmptyCell />;
                if (summaryItems.length === 0) {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Badge label={`${count}개 보유`} bg="#EFF6FF" color="#1D4ED8" border="#BFDBFE" />
                        </div>
                    );
                }

                return <ActivePassSummary items={summaryItems} tone="pt" />;
            },
        },
        {
            field: 'groupTickets',
            headerName: '그룹수강권',
            width: 360,
            align: 'center',
            customComponent: (_: any, row: ICenterMember) => {
                const allGtList = row.groupTickets || [];
                const summaryItems = toGroupPassSummaryItems(allGtList);
                const count = allGtList.length > 0 ? summaryItems.length : (row.groupTicketCount ?? 0);

                if (count === 0) return <EmptyCell />;
                if (summaryItems.length === 0) {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Badge label={`${count}개 보유`} bg="#F5F3FF" color="#7C3AED" border="#DDD6FE" />
                        </div>
                    );
                }

                return <ActivePassSummary items={summaryItems} tone="group" />;
            },
        },
        {
            field: 'hasSubscription',
            headerName: '이용권',
            width: 100,
            align: 'center',
            customComponent: (_value: boolean, row: any) => {
                const subList: any[] = row.subscriptions || [];
                const activeList = subList.filter((s: any) => s.status === 'active');
                const count = row.subscriptionCount || activeList.length;

                if (!count) return <EmptyCell />;

                const trigger = <Badge label={`${count}개`} bg="#FFFBEB" color="#B45309" border="#FDE68A" />;

                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            cursor: 'help',
                        }}
                    >
                        <HoverPopover trigger={trigger}>
                            <div
                                style={{
                                    fontWeight: 800,
                                    marginBottom: 10,
                                    fontSize: 14,
                                    color: '#F8FAFC',
                                }}
                            >
                                🏢 이용권 상세
                            </div>
                            {activeList.length === 0 ? (
                                <div style={{ fontSize: 12, color: '#CBD5E1', padding: '4px 0' }}>
                                    보유 중: <b style={{ color: '#F8FAFC' }}>{count}개</b>
                                    <div style={{ color: '#94A3B8', marginTop: 4, fontSize: 11 }}>상세 내역은 회원 프로필에서 확인할 수 있어요</div>
                                </div>
                            ) : (
                                activeList.map((sub: any, i: number) => {
                                    const dDay = sub.endDate ? dayjs(sub.endDate).diff(dayjs(), 'day') : null;
                                    const displayLockerNumber = sub.displayLockerNumber || sub.lockerNumber;
                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                padding: '6px 0',
                                                borderTop: i > 0 ? '1px solid #334155' : 'none',
                                                fontSize: 12,
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, color: '#F8FAFC' }}>
                                                {sub.productName}
                                                {displayLockerNumber && (
                                                    <span
                                                        style={{
                                                            marginLeft: 6,
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            padding: '1px 6px',
                                                            borderRadius: 4,
                                                            backgroundColor: '#3730A3',
                                                            color: '#DDD6FE',
                                                        }}
                                                    >
                                                        🔐 {displayLockerNumber}번
                                                    </span>
                                                )}
                                            </div>
                                            {sub.startDate && sub.endDate && (
                                                <div style={{ color: '#94A3B8', marginTop: 2 }}>
                                                    {dayjs(sub.startDate).format('YY.MM.DD')} ~ {dayjs(sub.endDate).format('YY.MM.DD')}
                                                </div>
                                            )}
                                            {dDay !== null && (
                                                <div
                                                    style={{
                                                        color: dDay <= 7 ? '#FBBF24' : '#94A3B8',
                                                        fontWeight: dDay <= 7 ? 600 : 400,
                                                    }}
                                                >
                                                    D-{dDay > 0 ? dDay : 0}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </HoverPopover>
                    </div>
                );
            },
        },
        {
            field: 'ptStatus',
            headerName: '상태',
            width: 90,
            align: 'center',
            customComponent: (_: any, row: ICenterMember) => {
                const s = getMemberDisplayStatus(row);
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge label={s.label} bg={s.bg} color={s.color} border={s.border} />
                    </div>
                );
            },
        },
        {
            field: 'lastLessonAt',
            headerName: '최근 수업',
            width: 124,
            align: 'center',
            isSort: true,
            customComponent: (value: any) => {
                if (!value) return <EmptyCell />;
                const d = dayjs(value);
                const daysAgo = dayjs().diff(d, 'day');
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3,
                        }}
                    >
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>{d.format('MM.DD')}</span>
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: daysAgo <= 7 ? '#059669' : daysAgo <= 30 ? '#6B7280' : '#DC2626',
                                backgroundColor: daysAgo <= 7 ? '#ECFDF5' : daysAgo <= 30 ? 'transparent' : '#FEF2F2',
                                padding: daysAgo <= 7 || daysAgo > 30 ? '3px 8px' : '0',
                                borderRadius: 999,
                            }}
                        >
                            {daysAgo === 0 ? '오늘' : `${daysAgo}일 전`}
                        </span>
                    </div>
                );
            },
        },
        {
            field: 'joinedAt',
            headerName: '가입일',
            width: 120,
            align: 'center',
            isSort: true,
            customComponent: (value: any) => {
                if (!value) return <EmptyCell />;
                return <span style={{ fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{dayjs(value).format('YY.MM.DD')}</span>;
            },
        },
    ];

    /* ═══════ 필터 초기화 ═══════ */
    const resetFilters = () => {
        setCoachFilter('all');
        setPeriodFilter('all');
        setSessionsFilter('all');
        setStatusFilter('all');
        setFacilityFilter('all');
        setAppAccountStatusFilter('all');
        setPage(1);
    };

    const activeFilterCount = [
        coachFilter !== 'all',
        periodFilter !== 'all',
        sessionsFilter !== 'all',
        statusFilter !== 'all',
        facilityFilter !== 'all',
        appAccountStatusFilter !== 'all',
    ].filter(Boolean).length;

    /* ═══════ 렌더 ═══════ */

    return (
        <Col
            style={{
                width: '100%',
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #F3F7FC 0%, #EEF4FB 100%)',
                marginTop: 64,
            }}
        >
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <Col
                style={{
                    width: '100%',
                    maxWidth: 1920,
                    margin: '0 auto',
                    padding: '32px 36px 56px',
                }}
            >
                {/* ── 헤더 카드 ── */}
                <div
                    style={{
                        padding: '30px 32px 26px',
                        borderRadius: 24,
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 55%, #EFF6FF 100%)',
                        border: '1px solid #DCE7F5',
                        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
                        marginBottom: 18,
                    }}
                >
                    <Row className="justify-between items-start" style={{ gap: 24, flexWrap: 'wrap' }}>
                        <Col style={{ gap: 10 }}>
                            <Row style={{ alignItems: 'center', gap: 10 }}>
                                <h1
                                    style={{
                                        fontSize: 34,
                                        fontWeight: 800,
                                        color: '#111827',
                                        margin: 0,
                                        letterSpacing: -1,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    통합 회원 관리
                                </h1>
                                {!showGuide && (
                                    <button
                                        onClick={() => {
                                            setShowGuide(true);
                                            localStorage.removeItem('guide_memberList');
                                        }}
                                        style={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            border: '1.5px solid #C4B5FD',
                                            backgroundColor: '#F5F3FF',
                                            color: '#7C3AED',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        ?
                                    </button>
                                )}
                            </Row>
                            <span style={{ fontSize: 15, color: '#6B7280', lineHeight: 1.6 }}>
                                수강 회원과 퍼블릭 회원을 한 화면에서 검색하고, 상태와 보유 상품을 빠르게 확인하세요.
                            </span>
                        </Col>
                        <Row className="gap-[10px]" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setIsBulkUploadVisible(true)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    height: 48,
                                    padding: '0 18px',
                                    borderRadius: 14,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    border: '1px solid #CBD5E1',
                                    backgroundColor: '#FFFFFF',
                                    color: '#334155',
                                    cursor: 'pointer',
                                    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
                                }}
                            >
                                <SvgIcon name={'SvgExcel'} size={18} fill={colors.trans} />
                                엑셀 일괄 등록
                            </button>
                            <button
                                onClick={() => navigate('/publicMember/register')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    height: 48,
                                    padding: '0 22px',
                                    borderRadius: 14,
                                    fontSize: 14,
                                    fontWeight: 800,
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                                    color: '#FFFFFF',
                                    cursor: 'pointer',
                                    boxShadow: '0 12px 24px rgba(37, 99, 235, 0.28)',
                                }}
                            >
                                + 회원 등록
                            </button>
                        </Row>
                    </Row>

                    <Row style={{ gap: 14, marginTop: 26, flexWrap: 'wrap' }}>
                        {dashboardStats.map((stat) => (
                            <Col
                                key={stat.label}
                                style={{
                                    flex: '1 1 220px',
                                    minWidth: 0,
                                    padding: '18px 18px 16px',
                                    borderRadius: 18,
                                    background: stat.bg,
                                    border: `1px solid ${stat.border}`,
                                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
                                }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: '#475569',
                                    }}
                                >
                                    {stat.label}
                                </span>
                                <span
                                    style={{
                                        display: 'block',
                                        marginTop: 8,
                                        fontSize: 30,
                                        fontWeight: 800,
                                        letterSpacing: -1,
                                        lineHeight: 1.05,
                                        color: stat.accent,
                                    }}
                                >
                                    {stat.value}
                                </span>
                                <span
                                    style={{
                                        display: 'block',
                                        marginTop: 10,
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        color: '#64748B',
                                    }}
                                >
                                    {stat.helper}
                                </span>
                            </Col>
                        ))}
                    </Row>
                </div>

                {showGuide && (
                    <Col
                        style={{
                            position: 'relative',
                            padding: '20px 24px',
                            borderRadius: 18,
                            backgroundColor: '#F5F3FF',
                            border: '1px solid #C4B5FD',
                            marginBottom: 18,
                        }}
                    >
                        <button
                            onClick={() => {
                                setShowGuide(false);
                                localStorage.setItem('guide_memberList', 'hidden');
                            }}
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 16,
                                background: 'none',
                                border: 'none',
                                fontSize: 16,
                                color: '#A78BFA',
                                cursor: 'pointer',
                            }}
                        >
                            ✕
                        </button>
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#5B21B6',
                                marginBottom: 8,
                            }}
                        >
                            수강 회원 & 퍼블릭 회원이란?
                        </span>
                        <Col
                            style={{
                                gap: 4,
                                fontSize: 13,
                                color: '#5B21B6',
                                lineHeight: 1.7,
                            }}
                        >
                            <span>
                                <b>🏋️ 수강 회원</b> : 트레이너/강사에게 배정되어 <b>PT·그룹수업 수강권</b>을 이용하는 회원입니다. 수업 일정과 수강권 차감이 함께
                                관리됩니다.
                            </span>
                            <span>
                                <b>🌐 퍼블릭 회원</b> : 트레이너 배정 없이 시설을 이용하는 회원입니다. <b>헬스 이용권·락커·라운지 등 퍼블릭 상품</b>을
                                구매·이용하며, 출입 및 키오스크 기능과 연동됩니다.
                            </span>
                        </Col>
                    </Col>
                )}

                {/* ── 검색/필터 카드 ── */}
                <div
                    style={{
                        padding: '22px 24px',
                        borderRadius: 22,
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
                        marginBottom: 18,
                    }}
                >
                    <Row className="items-center justify-between" style={{ gap: 16, flexWrap: 'wrap' }}>
                        <Row className="items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative', width: 360, maxWidth: '100%' }}>
                                <svg
                                    style={{
                                        position: 'absolute',
                                        left: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94A3B8',
                                    }}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    value={searchValue}
                                    onChange={(e) => {
                                        setSearchValue(e.target.value);
                                        setPage(1);
                                    }}
                                    placeholder="이름 또는 전화번호로 회원 검색"
                                    style={{
                                        width: '100%',
                                        height: 50,
                                        padding: '0 40px 0 46px',
                                        border: '1.5px solid #D9E2EC',
                                        borderRadius: 14,
                                        fontSize: 15,
                                        outline: 'none',
                                        backgroundColor: '#F8FAFC',
                                        color: '#334155',
                                        transition: 'all 0.15s',
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#3B82F6';
                                        e.target.style.backgroundColor = '#FFFFFF';
                                        e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#D9E2EC';
                                        e.target.style.backgroundColor = '#F8FAFC';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                {searchValue && (
                                    <button
                                        onClick={() => {
                                            setSearchValue('');
                                            setPage(1);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: 12,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 12,
                                            color: '#64748B',
                                            backgroundColor: '#E2E8F0',
                                            border: 'none',
                                            cursor: 'pointer',
                                            lineHeight: 1,
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={isMemberFetching}
                                title="목록 새로고침"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 50,
                                    height: 50,
                                    borderRadius: 14,
                                    border: '1.5px solid #D9E2EC',
                                    backgroundColor: '#F8FAFC',
                                    color: '#475569',
                                    cursor: isMemberFetching ? 'not-allowed' : 'pointer',
                                    opacity: isMemberFetching ? 0.6 : 1,
                                    flexShrink: 0,
                                }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{
                                        animation: isMemberFetching ? 'spin 0.8s linear infinite' : undefined,
                                    }}
                                >
                                    <polyline points="23 4 23 10 17 10" />
                                    <polyline points="1 20 1 14 7 14" />
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                            </button>
                        </Row>

                        <Row className="items-center gap-[10px]" style={{ flexWrap: 'wrap' }}>
                            {MEMBER_TYPE_OPTIONS.filter((opt) => opt.code !== 'archived' || hasArchives).map((opt) => {
                                const isSelected = memberTypeFilter === opt.code;
                                return (
                                    <button
                                        key={opt.code}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            height: 48,
                                            padding: '0 18px',
                                            borderRadius: 14,
                                            fontSize: 15,
                                            fontWeight: isSelected ? 800 : 700,
                                            border: isSelected ? '1.5px solid #3B82F6' : '1.5px solid #E5E7EB',
                                            backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
                                            color: isSelected ? '#1D4ED8' : '#334155',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            boxShadow: isSelected ? '0 8px 18px rgba(59,130,246,0.12)' : 'none',
                                        }}
                                        onClick={() => {
                                            setMemberTypeFilter(opt.code);
                                            setPage(1);
                                        }}
                                    >
                                        <span style={{ fontSize: 17 }}>{opt.icon}</span>
                                        {opt.value}
                                    </button>
                                );
                            })}

                            <button
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    height: 48,
                                    padding: '0 18px',
                                    borderRadius: 14,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    border: hasAdvancedFilter ? '1.5px solid #7C3AED' : '1.5px solid #E5E7EB',
                                    backgroundColor: hasAdvancedFilter ? '#F5F3FF' : '#FFFFFF',
                                    color: hasAdvancedFilter ? '#6D28D9' : '#334155',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setShowFilters((prev) => !prev)}
                            >
                                <svg
                                    width="17"
                                    height="17"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                                </svg>
                                필터
                                {hasAdvancedFilter && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 24,
                                            height: 24,
                                            borderRadius: 999,
                                            backgroundColor: '#7C3AED',
                                            color: '#FFFFFF',
                                            fontSize: 12,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                        </Row>
                    </Row>

                    {showFilters && (
                        <Row
                            className="items-stretch flex-wrap"
                            style={{
                                marginTop: 18,
                                gap: 14,
                                padding: '18px 18px',
                                background: 'linear-gradient(180deg, #FBFCFE 0%, #F8FAFC 100%)',
                                borderRadius: 18,
                                border: '1px solid #E2E8F0',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    padding: '12px 14px',
                                    borderRadius: 14,
                                    border: '1px solid #E2E8F0',
                                    backgroundColor: '#FFFFFF',
                                }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748B' }}>공통</span>
                                <Row className="items-center flex-wrap" style={{ gap: 14 }}>
                                    <Row className="items-center gap-[10px]">
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>강사</span>
                                        <select
                                            value={coachFilter}
                                            onChange={(e) => {
                                                setCoachFilter(e.target.value);
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 44,
                                                padding: '0 14px',
                                                borderRadius: 12,
                                                border: '1.5px solid #D1D5DB',
                                                fontSize: 14,
                                                backgroundColor: '#FFFFFF',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                color: coachFilter !== 'all' ? '#1D4ED8' : '#374151',
                                                fontWeight: coachFilter !== 'all' ? 700 : 500,
                                                minWidth: 132,
                                            }}
                                        >
                                            <option value="all">전체</option>
                                            {coachList.map((c: any) => {
                                                const name = c.name ?? c.coachName ?? '';
                                                return (
                                                    <option key={c.coachId} value={name}>
                                                        {name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </Row>

                                    <Row className="items-center gap-[10px]">
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>앱 상태</span>
                                        <select
                                            value={appAccountStatusFilter}
                                            onChange={(e) => {
                                                setAppAccountStatusFilter(e.target.value as AppAccountStatus | 'all');
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 44,
                                                padding: '0 14px',
                                                borderRadius: 12,
                                                border: '1.5px solid #D1D5DB',
                                                fontSize: 14,
                                                backgroundColor: '#FFFFFF',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                color: appAccountStatusFilter !== 'all' ? '#1D4ED8' : '#374151',
                                                fontWeight: appAccountStatusFilter !== 'all' ? 700 : 500,
                                                minWidth: 132,
                                            }}
                                        >
                                            <option value="all">전체</option>
                                            <option value="linked">연동</option>
                                            <option value="withdrawn">앱 탈퇴</option>
                                            <option value="unlinked">미연동</option>
                                        </select>
                                    </Row>
                                </Row>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    padding: '12px 14px',
                                    borderRadius: 14,
                                    border: '1px solid #E2E8F0',
                                    backgroundColor: '#FFFFFF',
                                }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748B' }}>수강권</span>
                                <Row className="items-center flex-wrap" style={{ gap: 14 }}>
                                    <Row className="items-center gap-[10px]">
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>잔여 기간</span>
                                        <select
                                            value={periodFilter}
                                            onChange={(e) => {
                                                setPeriodFilter(e.target.value);
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 44,
                                                padding: '0 14px',
                                                borderRadius: 12,
                                                border: '1.5px solid #D1D5DB',
                                                fontSize: 14,
                                                backgroundColor: '#FFFFFF',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                color: periodFilter !== 'all' ? '#1D4ED8' : '#374151',
                                                fontWeight: periodFilter !== 'all' ? 700 : 500,
                                                minWidth: 132,
                                            }}
                                        >
                                            <option value="all">전체</option>
                                            <option value="7">&lt; 7일</option>
                                            <option value="14">&lt; 14일</option>
                                            <option value="30">&lt; 30일</option>
                                            <option value="100">&lt; 100일</option>
                                            <option value="expired">기간 만료</option>
                                        </select>
                                    </Row>

                                    <Row className="items-center gap-[10px]">
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>잔여 횟수</span>
                                        <select
                                            value={sessionsFilter}
                                            onChange={(e) => {
                                                setSessionsFilter(e.target.value);
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 44,
                                                padding: '0 14px',
                                                borderRadius: 12,
                                                border: '1.5px solid #D1D5DB',
                                                fontSize: 14,
                                                backgroundColor: '#FFFFFF',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                color: sessionsFilter !== 'all' ? '#1D4ED8' : '#374151',
                                                fontWeight: sessionsFilter !== 'all' ? 700 : 500,
                                                minWidth: 132,
                                            }}
                                        >
                                            <option value="all">전체</option>
                                            <option value="3">&lt; 3회</option>
                                            <option value="5">&lt; 5회</option>
                                            <option value="10">&lt; 10회</option>
                                            <option value="15">&lt; 15회</option>
                                            <option value="20">&lt; 20회</option>
                                        </select>
                                    </Row>

                                    <Row className="items-center gap-[10px]">
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>상태</span>
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => {
                                                setStatusFilter(e.target.value);
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 44,
                                                padding: '0 14px',
                                                borderRadius: 12,
                                                border: '1.5px solid #D1D5DB',
                                                fontSize: 14,
                                                backgroundColor: '#FFFFFF',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                color: statusFilter !== 'all' ? '#1D4ED8' : '#374151',
                                                fontWeight: statusFilter !== 'all' ? 700 : 500,
                                                minWidth: 132,
                                            }}
                                        >
                                            <option value="all">전체</option>
                                            <option value="active">활성</option>
                                            <option value="pending">대기</option>
                                            <option value="expired">만료</option>
                                        </select>
                                    </Row>
                                </Row>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    padding: '12px 14px',
                                    borderRadius: 14,
                                    border: '1px solid #BFDBFE',
                                    backgroundColor: '#F8FBFF',
                                }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 800, color: '#1D4ED8' }}>이용권</span>
                                <Row className="items-center gap-[10px]">
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>이용권 상태</span>
                                    <select
                                        value={facilityFilter}
                                        onChange={(e) => {
                                            setFacilityFilter(e.target.value as FacilityStatus | 'all');
                                            setPage(1);
                                        }}
                                        style={{
                                            height: 44,
                                            padding: '0 14px',
                                            borderRadius: 12,
                                            border: '1.5px solid #D1D5DB',
                                            fontSize: 14,
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            color: facilityFilter !== 'all' ? '#1D4ED8' : '#374151',
                                            fontWeight: facilityFilter !== 'all' ? 700 : 500,
                                            minWidth: 160,
                                        }}
                                    >
                                        <option value="all">전체</option>
                                        <option value="active">이용 중</option>
                                        <option value="expiring">7일 안에 만료</option>
                                        <option value="expired">만료</option>
                                    </select>
                                </Row>
                            </div>

                            {hasAdvancedFilter && (
                                <button
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        alignSelf: 'center',
                                        gap: 6,
                                        fontSize: 14,
                                        color: '#DC2626',
                                        cursor: 'pointer',
                                        background: '#FFFFFF',
                                        border: '1.5px solid #FCA5A5',
                                        fontWeight: 800,
                                        height: 44,
                                        padding: '0 16px',
                                        borderRadius: 12,
                                    }}
                                    onClick={resetFilters}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                    필터 초기화
                                </button>
                            )}
                        </Row>
                    )}

                    {(hasAdvancedFilter || searchValue) && (
                        <Row className="gap-[8px] flex-wrap" style={{ marginTop: 14 }}>
                            {searchValue && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#EFF6FF',
                                        color: '#1D4ED8',
                                        border: '1px solid #BFDBFE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setSearchValue('');
                                        setPage(1);
                                    }}
                                >
                                    검색: {searchValue} ✕
                                </span>
                            )}
                            {coachFilter !== 'all' && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#F5F3FF',
                                        color: '#6D28D9',
                                        border: '1px solid #DDD6FE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setCoachFilter('all');
                                        setPage(1);
                                    }}
                                >
                                    강사: {coachFilter} ✕
                                </span>
                            )}
                            {periodFilter !== 'all' && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#F5F3FF',
                                        color: '#6D28D9',
                                        border: '1px solid #DDD6FE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setPeriodFilter('all');
                                        setPage(1);
                                    }}
                                >
                                    수강권 기간: {periodFilter === 'expired' ? '만료' : `${periodFilter}일 이내`} ✕
                                </span>
                            )}
                            {sessionsFilter !== 'all' && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#F5F3FF',
                                        color: '#6D28D9',
                                        border: '1px solid #DDD6FE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setSessionsFilter('all');
                                        setPage(1);
                                    }}
                                >
                                    수강권 횟수: {sessionsFilter}회 이하 ✕
                                </span>
                            )}
                            {statusFilter !== 'all' && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#F5F3FF',
                                        color: '#6D28D9',
                                        border: '1px solid #DDD6FE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setStatusFilter('all');
                                        setPage(1);
                                    }}
                                >
                                    수강권 상태: {statusFilter === 'active' ? '활성' : statusFilter === 'pending' ? '대기' : '만료'} ✕
                                </span>
                            )}
                            {facilityFilter !== 'all' && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#EFF6FF',
                                        color: '#1D4ED8',
                                        border: '1px solid #BFDBFE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setFacilityFilter('all');
                                        setPage(1);
                                    }}
                                >
                                    이용권: {FACILITY_STATUS_FILTER_LABEL[facilityFilter]} ✕
                                </span>
                            )}
                            {appAccountStatusFilter !== 'all' && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        borderRadius: 999,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        backgroundColor: '#F5F3FF',
                                        color: '#6D28D9',
                                        border: '1px solid #DDD6FE',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        setAppAccountStatusFilter('all');
                                        setPage(1);
                                    }}
                                >
                                    앱 상태: {APP_ACCOUNT_FILTER_LABEL[appAccountStatusFilter]} ✕
                                </span>
                            )}
                        </Row>
                    )}
                </div>

                {/* ── 테이블 영역 ── */}
                <div
                    style={{
                        padding: '24px',
                        borderRadius: 24,
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 34px rgba(15, 23, 42, 0.05)',
                    }}
                >
                    <Row className="justify-between items-end" style={{ marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
                        <Col style={{ gap: 6 }}>
                            <span
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: '#111827',
                                    letterSpacing: -0.6,
                                }}
                            >
                                {isArchiveTab ? '과거 회원' : '회원 목록'}
                            </span>
                            <span style={{ fontSize: 14, color: '#64748B', lineHeight: 1.6 }}>
                                {isArchiveTab
                                    ? '담당 강사가 삭제(바로 삭제)되어 더 이상 관리되지 않는 회원입니다. 새 강사를 배정하면 자동으로 복귀됩니다.'
                                    : '회원 상태, 담당 강사, 수강권과 이용권 보유 현황을 한 번에 확인할 수 있습니다.'}
                            </span>
                        </Col>
                    </Row>

                    {/* ── 누적 선택 chip 영역 (검색·필터로 행이 사라져도 선택 유지 확인용) ── */}
                    {!isArchiveTab && selectedIds.size > 0 && (
                        <div
                            style={{
                                marginBottom: 16,
                                padding: '12px 16px',
                                borderRadius: 16,
                                backgroundColor: '#F5F3FF',
                                border: '1px solid #DDD6FE',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#6D28D9', marginRight: 4 }}>
                                선택한 회원 {selectedIds.size}명
                            </span>
                            {selectedMembers.map((m) => (
                                <span
                                    key={m.memberId}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 10px',
                                        borderRadius: 999,
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #DDD6FE',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#4C1D95',
                                    }}
                                >
                                    {m.name || '이름 없음'}
                                    <button
                                        type="button"
                                        onClick={() => removeFromSelection(m.memberId)}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 16,
                                            height: 16,
                                            borderRadius: 999,
                                            border: 'none',
                                            backgroundColor: '#EDE9FE',
                                            color: '#6D28D9',
                                            fontSize: 11,
                                            fontWeight: 800,
                                            cursor: 'pointer',
                                            lineHeight: 1,
                                        }}
                                        aria-label={`${m.name} 선택 해제`}
                                    >
                                        ✕
                                    </button>
                                </span>
                            ))}
                            {/* 선택은 있는데 allMembers 에 더 이상 없는 경우 (예: 다른 사용자가 삭제) */}
                            {selectedIds.size > selectedMembers.length && (
                                <span style={{ fontSize: 12, color: '#94A3B8' }}>
                                    + {selectedIds.size - selectedMembers.length}명 (목록 갱신 필요)
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={clearSelection}
                                style={{
                                    marginLeft: 'auto',
                                    padding: '6px 12px',
                                    borderRadius: 999,
                                    border: '1px solid #DDD6FE',
                                    backgroundColor: '#FFFFFF',
                                    color: '#6D28D9',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                }}
                            >
                                전체 해제
                            </button>
                        </div>
                    )}

                    {isArchiveTab ? (
                        isArchiveLoading || isArchiveListIncomplete ? (
                            renderEmptyState({
                                icon: '⏳',
                                title: '과거 회원 목록을 불러오는 중입니다',
                                description: '아카이브된 회원 정보를 정리하고 있습니다. 잠시만 기다려주세요.',
                            })
                        ) : (archiveData?.results ?? []).length === 0 ? (
                            renderEmptyState({
                                icon: '📋',
                                title: '과거 회원 기록이 없습니다',
                                description: '강사 삭제(바로 삭제) 시 아카이브된 회원 기록이 여기에 표시됩니다.',
                            })
                        ) : (
                            <Grid
                                key="archive-grid"
                                colDefs={[
                                    {
                                        field: 'memberName',
                                        headerName: '이름',
                                        width: 150,
                                        align: 'center',
                                        fontType: 'Body2Bold',
                                    },
                                    {
                                        field: 'phoneNumber',
                                        headerName: '연락처',
                                        width: 170,
                                        align: 'center',
                                        nullValue: '-',
                                        fontType: 'Body2Regular',
                                        customFormat: (value: string) => formatDomesticPhoneNumber(value),
                                    },
                                    {
                                        field: 'coachName',
                                        headerName: '당시 담당강사',
                                        width: 150,
                                        align: 'center',
                                        fontType: 'Body2Regular',
                                    },
                                    {
                                        field: 'archivedAt',
                                        headerName: '아카이브일',
                                        width: 140,
                                        align: 'center',
                                        customComponent: (value: any) => (
                                            <span style={{ fontSize: 13, color: '#64748B' }}>{value ? dayjs(value).format('YYYY.MM.DD') : '-'}</span>
                                        ),
                                    },
                                    {
                                        field: 'reason',
                                        headerName: '사유',
                                        width: 120,
                                        align: 'center',
                                        customComponent: () => <span style={{ fontSize: 12, color: '#94A3B8' }}>강사 삭제</span>,
                                    },
                                ]}
                                rowData={archiveData?.results ?? []}
                                leftTopCustomComponent={() => (
                                    <Col style={{ marginRight: 4 }}>
                                        <span
                                            style={{
                                                display: 'block',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: '#64748B',
                                            }}
                                        >
                                            과거 회원
                                        </span>
                                        <span
                                            style={{
                                                display: 'block',
                                                marginTop: 2,
                                                fontSize: 26,
                                                fontWeight: 800,
                                                color: '#0F172A',
                                                letterSpacing: -0.8,
                                                lineHeight: 1.1,
                                            }}
                                        >
                                            {archiveData?.totalResults ?? 0}
                                            <span
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: '#94A3B8',
                                                    marginLeft: 4,
                                                }}
                                            >
                                                명
                                            </span>
                                        </span>
                                    </Col>
                                )}
                            />
                        )
                    ) : isMemberLoading ? (
                        renderEmptyState({
                            icon: '⏳',
                            title: '회원 목록을 불러오는 중입니다',
                            description: '첫 페이지를 빠르게 불러오고 있습니다. 잠시만 기다려주세요.',
                        })
                    ) : members.length === 0 ? (
                        searchValue || hasAdvancedFilter || memberTypeFilter !== 'all' ? (
                            renderEmptyState({
                                icon: '🔍',
                                title: '조건에 맞는 회원이 없습니다',
                                description: '검색어나 필터 조건을 조금 완화하면 다른 회원을 바로 확인할 수 있습니다.',
                                actionLabel: hasAdvancedFilter ? '필터 초기화' : '검색 지우기',
                                actionOnClick: hasAdvancedFilter
                                    ? resetFilters
                                    : () => {
                                          setSearchValue('');
                                          setPage(1);
                                      },
                            })
                        ) : (
                            renderEmptyState({
                                icon: '👤',
                                title: '등록된 회원이 없습니다',
                                description: '직접 회원을 등록하거나 엑셀 일괄 등록으로 회원 목록을 빠르게 채울 수 있습니다.',
                                actionLabel: '회원 등록하기',
                                actionOnClick: () => navigate('/publicMember/register'),
                            })
                        )
                    ) : (
                        <Grid
                            // key 에 searchValue/필터를 넣어 Grid 를 통째로 remount 하면
                            // 검색 변경 시 내부 selection state 가 초기화되어 선택이 사라짐.
                            // controlled selection 으로 옮겼으니 key 는 고정.
                            key="member-grid"
                            colDefs={colDefs}
                            rowData={members}
                            rowKey="memberId"
                            selectedKeys={selectedIds}
                            onToggleSelect={(id, row) => toggleSelect(id, row)}
                            onToggleAllVisible={(ids, rows) => toggleAllVisible(ids, rows)}
                            onClickRow={(row: any) => {
                                /*
                                 * 경로 정의는 `lib/memberRoutes` 한 곳에만 둔다.
                                 * 오늘의 시그널 카드도 **같은 함수**를 쓴다 — 예전에
                                 * 거기가 구 화면(`/member/detail`)으로 가 있었고,
                                 * 문자열이 흩어져 있어서 아무도 못 알아챘다.
                                 */
                                navigate(memberDetailPath(row.memberId), {
                                    state: memberDetailState(row.memberId),
                                });
                            }}
                            isCheckColumn
                            isAllCheckColumn={false}
                            isInfinity
                            rowHeight={52}
                            keepPageHeight
                            fixedPageRowCount={pageLimit}
                            leftTopCustomComponent={() => (
                                <Row className="items-center flex-wrap" style={{ gap: 12, padding: '2px 0' }}>
                                    <Col style={{ marginRight: 4 }}>
                                        <span
                                            style={{
                                                display: 'block',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: '#64748B',
                                            }}
                                        >
                                            현재 표시
                                        </span>
                                        <span
                                            style={{
                                                display: 'block',
                                                marginTop: 2,
                                                fontSize: 26,
                                                fontWeight: 800,
                                                color: '#0F172A',
                                                letterSpacing: -0.8,
                                                lineHeight: 1.1,
                                            }}
                                        >
                                            {members.length}
                                            <span
                                                style={{
                                                    fontSize: 15,
                                                    marginLeft: 4,
                                                    color: '#94A3B8',
                                                }}
                                            >
                                                / {displayedTotalResults.toLocaleString()}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 16,
                                                    marginLeft: 4,
                                                    color: '#64748B',
                                                }}
                                            >
                                                명
                                            </span>
                                        </span>
                                    </Col>

                                    <label
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            height: 38,
                                            padding: '0 12px',
                                            borderRadius: 12,
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            color: '#475569',
                                            fontSize: 13,
                                            fontWeight: 800,
                                        }}
                                    >
                                        <span>페이지당</span>
                                        <select
                                            value={pageLimit}
                                            onChange={(e) => {
                                                setPageLimit(Number(e.target.value));
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 28,
                                                border: 'none',
                                                outline: 'none',
                                                backgroundColor: 'transparent',
                                                color: '#1D4ED8',
                                                fontSize: 13,
                                                fontWeight: 900,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {MEMBER_LIST_PAGE_SIZE_OPTIONS.map((size) => (
                                                <option key={size} value={size}>
                                                    {size}명
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <button
                                        className={`${members.length ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                                        onClick={() => members.length && exportToExcel(members)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 7,
                                            padding: '10px 14px',
                                            borderRadius: 12,
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#F8FAFC',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: '#334155',
                                            cursor: members.length ? 'pointer' : 'not-allowed',
                                        }}
                                    >
                                        <SvgIcon name={'SvgExcel'} size={16} fill={colors.trans} />
                                        현재 페이지 엑셀
                                    </button>

                                    <button
                                        className={`${displayedTotalResults && !isExportingAll ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                                        onClick={exportAllMembersToExcel}
                                        disabled={!displayedTotalResults || isExportingAll}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 7,
                                            padding: '10px 14px',
                                            borderRadius: 12,
                                            border: '1px solid #BFDBFE',
                                            backgroundColor: '#EFF6FF',
                                            fontSize: 13,
                                            fontWeight: 800,
                                            color: '#1D4ED8',
                                            cursor: displayedTotalResults && !isExportingAll ? 'pointer' : 'not-allowed',
                                        }}
                                    >
                                        <SvgIcon name={'SvgExcel'} size={16} fill={colors.trans} />
                                        {isExportingAll ? '준비 중' : '전체 엑셀'}
                                    </button>

                                    {selectedIds.size > 0 && (
                                        <Badge label={`선택 ${selectedIds.size}명`} bg="#F5F3FF" color="#6D28D9" border="#DDD6FE" size="sm" />
                                    )}
                                </Row>
                            )}
                            leftBottomCustomComponent={() => {
                                const disabled = selectedIds.size === 0;
                                return (
                                    <button
                                        onClick={() => {
                                            if (disabled) return;
                                            setIsDeleteModalVisible(true);
                                        }}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            height: 46,
                                            padding: '0 18px',
                                            borderRadius: 999,
                                            border: disabled ? '1px solid #E2E8F0' : '1px solid #FBCACA',
                                            backgroundColor: disabled ? '#FFFFFF' : '#FFF1F2',
                                            color: disabled ? '#94A3B8' : '#BE123C',
                                            fontSize: 14,
                                            fontWeight: 800,
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            boxShadow: disabled ? 'none' : '0 10px 18px rgba(244, 63, 94, 0.08)',
                                        }}
                                    >
                                        <span>선택 삭제</span>
                                        {!disabled && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: 24,
                                                    height: 24,
                                                    padding: '0 8px',
                                                    borderRadius: 999,
                                                    backgroundColor: '#FFE4E6',
                                                    color: '#BE123C',
                                                    fontSize: 12,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {selectedIds.size}
                                            </span>
                                        )}
                                    </button>
                                );
                            }}
                            centerTopCustomComponent={renderMemberServerPagination}
                            centerBottomCustomComponent={renderMemberServerPagination}
                        />
                    )}
                </div>
            </Col>

            {/* 삭제 확인 모달 */}
            {isDeleteModalVisible && (
                <ModalAlert
                    modalVisible={isDeleteModalVisible}
                    setModalVisible={setIsDeleteModalVisible}
                    title="회원 삭제"
                    content={`선택한 ${selectedIds.size}명의 회원을 삭제하시겠습니까?`}
                    cancelButtonText="취소"
                    confirmButtonText="삭제"
                    callBack={handleDeleteSelected}
                    cancelCallBack={() => setIsDeleteModalVisible(false)}
                />
            )}

            {/* 엑셀 일괄 등록 안내 모달 */}
            <PublicMemberBulkUploadModal isVisible={isBulkUploadVisible} onClose={() => setIsBulkUploadVisible(false)} centerName={authObject?.centerName} />

            {/* 타입 전환 모달 */}
            <MemberTypeConvertModal
                isVisible={!!convertTarget}
                onClose={() => setConvertTarget(null)}
                member={convertTarget}
                centerId={centerId}
                createAlert={createAlert}
            />

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default MemberlistPage;
